// Story 7.1 — TanStack Query mutation for GET /api/v1/auth/verify-email?token=<>.
//
// FIRST SPA consumer of the Story 1.10 verify-email endpoint. The
// backend endpoint returns a 302 redirect to /welcome on success, so
// we use `redirect: "manual"` to observe the redirect ourselves and
// then navigate via React Router (no full page reload).
//
// On failure (VerificationTokenInvalid → 400, VerificationTokenExpired
// → 410), the response is a JSON error envelope we surface to the
// caller. The consumer (VerifyEmailPage) renders the "link expired
// or been used" copy on any error.

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface VerifyEmailVariables {
  token: string;
}

export interface VerifyEmailOutcome {
  verified: true;
}

export interface VerifyEmailError {
  code: string;
  message: string;
  status: number;
}

async function requestVerifyEmail(
  variables: VerifyEmailVariables,
): Promise<VerifyEmailOutcome> {
  const url = `/api/v1/auth/verify-email?token=${encodeURIComponent(variables.token)}`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    redirect: "manual",
  });
  // manual redirect: on 302 response.type === "opaqueredirect"; that's success.
  if (response.type === "opaqueredirect") {
    return { verified: true };
  }
  if (response.ok) {
    // Defense-in-depth: if backend ever switches to 200-with-JSON.
    attachRequestIdToScope(response);
    return { verified: true };
  }
  attachRequestIdToScope(response);
  let detail: VerifyEmailError = {
    code: "VERIFY_EMAIL_FAILED",
    message: `Verification failed (HTTP ${response.status})`,
    status: response.status,
  };
  try {
    const raw = await response.json();
    if (raw?.error) {
      detail = {
        code: raw.error.code,
        message: raw.error.message,
        status: response.status,
      };
    }
  } catch {
    // Body not JSON — keep default.
  }
  throw detail;
}

export function useVerifyEmail(): UseMutationResult<
  VerifyEmailOutcome,
  VerifyEmailError,
  VerifyEmailVariables
> {
  return useMutation<
    VerifyEmailOutcome,
    VerifyEmailError,
    VerifyEmailVariables
  >({
    mutationFn: requestVerifyEmail,
    retry: false,
  });
}
